import { APIStatus } from "@/api/APIStatus.type";
import Button from "@/components/Button";
import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import { Button as NewButton } from "@/design-system/Button/Button";
import { cross, thickArrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { defaultSquadBuilderSelector } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { useTranslation } from "@repo/shared/i18n";
import { usesClubPool } from "@repo/shared/redux";
import { useSelector } from "react-redux";

const ArrowButton = ({
  onClick,
  direction,
}: {
  onClick: () => void;
  direction: "left" | "right";
}) => {
  return (
    <button
      className={cn(
        "flex items-center justify-center mx-auto w-full bg-action-tertiary h-full soft-dark-bottom rounded-sm",
        direction === "left" && "rotate-180",
      )}
      onClick={onClick}
    >
      {thickArrow}
    </button>
  );
};
const FloatingBottomNavigation = ({
  handleClickedBuildSquads,
  changeBuiltSquadsIndex,
  currentBuiltSquadsIndex,
  totalAmountOfBuiltSquads,
  statusBuildSquad,
  handleCloseSuggestedSquads,
  handleChoseSquad,
}: {
  handleClickedBuildSquads: () => void;
  changeBuiltSquadsIndex: (index: number) => void;
  currentBuiltSquadsIndex: number;
  totalAmountOfBuiltSquads: number;
  statusBuildSquad: APIStatus;
  handleCloseSuggestedSquads: () => void;
  handleChoseSquad: (index: number) => void;
}) => {
  const { t } = useTranslation(["squadBuilder", "common"]);
  /*
    This bar is the touch-device twin of SuggestSquadsButton and has to carry
    the same price — without it, a mobile user spends a credit with nothing on
    screen saying so (DEV-3402). Scope comes from the slice rather than through
    CenterPanel, which only the default squad builder ever renders.
  */
  const { scope } = useSelector(defaultSquadBuilderSelector);

  return statusBuildSquad === "fulfilled" ? (
    <div
      className={cn(
        "flex items-center justify-center p-3 z-20 fixed left-1/2 -translate-x-1/2 rounded-xl touch-device:hidden shadow-build-squad",
        statusBuildSquad === "fulfilled" && "bg-inner-box w-[95%] max-w-150",
      )}
      style={{
        bottom: "calc(var(--ad-gutter) + 10px)",
      }}
    >
      <div className="flex flex-col w-full gap-y-2">
        <div className="flex flex-row items-center justify-between">
          {/* eslint-disable-next-line i18next/no-literal-string -- invisible spacer, never shown */}
          <button className="invisible">close</button>
          <label className="text-center text-dark-primary font-semibold">
            {currentBuiltSquadsIndex + 1}/{totalAmountOfBuiltSquads}
          </label>
          <button
            onClick={handleCloseSuggestedSquads}
            className="p-1 stroke-dark-tertiary"
          >
            <div className="w-4 h-4">{cross}</div>
          </button>
        </div>
        <div className="flex flex-row items-center justify-center gap-x-2 w-full text-dark-secondary h-8">
          <ArrowButton
            onClick={() => changeBuiltSquadsIndex(currentBuiltSquadsIndex - 1)}
            direction="left"
          />
          <ArrowButton
            onClick={() => changeBuiltSquadsIndex(currentBuiltSquadsIndex + 1)}
            direction="right"
          />
          <div className="w-1/2 shrink-0">
            <Button
              variant={"primary"}
              label={t("common:select")}
              onClick={() => handleChoseSquad(currentBuiltSquadsIndex)}
              large
            />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div
      className="fixed right-2 z-100 touch-device:hidden"
      style={{
        bottom: "calc(var(--ad-gutter) + 10px)",
      }}
    >
      <NewButton
        variant="primary"
        disabled={statusBuildSquad === "pending"}
        size="medium"
        onClick={handleClickedBuildSquads}
      >
        {t("build.buildSquads")}
      </NewButton>
      {/* Out of flow, hanging off the left edge — this bar is pinned above
      the mobile nav, so the price must not add height to it or nudge
      the button off centre. */}
      {statusBuildSquad !== "pending" && (
        <CreditCostMarker
          featureId="build-squad-club"
          when={usesClubPool(scope)}
          placement="beside-button"
        />
      )}
    </div>
  );
};

export default FloatingBottomNavigation;
